import { create } from 'zustand';
import { projectsAPI, Project, ProjectCreate, ProjectUpdate } from '../services/api';

interface ProjectState {
  projects: Project[];
  selectedProject: Project | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchProjects: (clientId?: string, includeInactive?: boolean) => Promise<void>;
  getProject: (id: string) => Promise<Project | null>;
  createProject: (data: ProjectCreate) => Promise<Project>;
  updateProject: (id: string, data: ProjectUpdate) => Promise<Project>;
  deleteProject: (id: string) => Promise<void>;
  setSelectedProject: (project: Project | null) => void;
  clearError: () => void;
}

export const useProjectStore = create<ProjectState>((set, get) => ({
  projects: [],
  selectedProject: null,
  isLoading: false,
  error: null,

  fetchProjects: async (clientId?: string, includeInactive = false) => {
    set({ isLoading: true, error: null });
    try {
      const projects = await projectsAPI.list(clientId, includeInactive);
      set({ projects, isLoading: false });
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || 'Failed to fetch projects',
        isLoading: false
      });
    }
  },

  getProject: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const project = await projectsAPI.get(id);
      set({ selectedProject: project, isLoading: false });
      return project;
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || 'Failed to fetch project',
        isLoading: false
      });
      return null;
    }
  },

  createProject: async (data: ProjectCreate) => {
    set({ isLoading: true, error: null });
    try {
      const project = await projectsAPI.create(data);
      set(state => ({
        projects: [...state.projects, project].sort((a, b) =>
          (a.client_name || '').localeCompare(b.client_name || '') || a.name.localeCompare(b.name)
        ),
        isLoading: false
      }));
      return project;
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || 'Failed to create project',
        isLoading: false
      });
      throw error;
    }
  },

  updateProject: async (id: string, data: ProjectUpdate) => {
    set({ isLoading: true, error: null });
    try {
      const project = await projectsAPI.update(id, data);
      set(state => ({
        projects: state.projects
          .map(p => p.id === id ? project : p)
          .sort((a, b) =>
            (a.client_name || '').localeCompare(b.client_name || '') || a.name.localeCompare(b.name)
          ),
        selectedProject: state.selectedProject?.id === id ? project : state.selectedProject,
        isLoading: false
      }));
      return project;
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || 'Failed to update project',
        isLoading: false
      });
      throw error;
    }
  },

  deleteProject: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await projectsAPI.delete(id);
      set(state => ({
        projects: state.projects.filter(p => p.id !== id),
        selectedProject: state.selectedProject?.id === id ? null : state.selectedProject,
        isLoading: false
      }));
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || 'Failed to delete project',
        isLoading: false
      });
      throw error;
    }
  },

  setSelectedProject: (project) => set({ selectedProject: project }),

  clearError: () => set({ error: null }),
}));
